import type { RowData } from "@tanstack/react-table";
import type { DataGridColumnInstance } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { getPinGrowthLimit } from "../layout/stickyPins";
import { commitResizeWidths } from "./commitResizeWidths";
import { measureColumnContent } from "./fitColumn";

/** The edge that moves when the column grows: `start` for end-pinned columns. */
export type ResizeEdge = "start" | "end";

interface ColumnResizerOptions<TData extends RowData> {
    column: DataGridColumnInstance<TData>;
    controller: GridController;
    edge: ResizeEdge;
}

/**
 * Width changes for one column edge, shared by the header handle and the
 * body's column borders. Rendered widths are committed first
 * (`commitResizeWidths`), so only the dragged edge moves. A width stops at
 * `minSize` but not at the default cap (`meta.defaultMaxSize`); a pinned
 * column grows only as far as its side can stay pinned (`getPinGrowthLimit`).
 */
export const createColumnResizer = <TData extends RowData>({
    column,
    controller,
    edge,
}: ColumnResizerOptions<TData>) => {
    const table = column.table;
    const direction = edge === "end" ? 1 : -1;

    const getRenderedWidth = (
        candidate: DataGridColumnInstance<TData> = column,
    ) => controller.layout.widths[candidate.id] ?? candidate.getSize();

    const setWidth = (width: number) => {
        const next = Math.max(width, column.columnDef.minSize ?? 0);
        table.setColumnSizing((old) => ({ ...old, [column.id]: next }));
    };

    const commitWidths = () => commitResizeWidths({ column, controller, edge });

    const getMaxWidth = (startWidth: number) => {
        const pin = column.getIsPinned();
        if (!pin) {
            return Number.POSITIVE_INFINITY;
        }
        const sumWidths = (columns: Array<typeof column>) =>
            columns.reduce(
                (sum, candidate) => sum + getRenderedWidth(candidate),
                0,
            );
        return (
            startWidth +
            getPinGrowthLimit({
                side: pin,
                containerWidth: controller.containerWidth.get(),
                pinnedStartWidth: sumWidths(table.getStartVisibleLeafColumns()),
                pinnedEndWidth: sumWidths(table.getEndVisibleLeafColumns()),
                centerWidths: table
                    .getCenterVisibleLeafColumns()
                    .map((candidate) => getRenderedWidth(candidate)),
            })
        );
    };

    /** Grows or shrinks by `delta` px in the edge's direction. */
    const resizeBy = (delta: number) => {
        const width = getRenderedWidth();
        const release = commitWidths();
        setWidth(Math.min(width + delta * direction, getMaxWidth(width)));
        release();
    };

    /** Sets the measured content width, capped like a drag. */
    const fitToContent = (root: HTMLElement | null) => {
        const width = measureColumnContent({ column, root });
        if (width === null) {
            return;
        }
        const release = commitWidths();
        setWidth(Math.min(width, getMaxWidth(getRenderedWidth())));
        release();
    };

    /**
     * Pointer drag from `startX`; the moving edge follows the pointer. Widths
     * are committed on the first move, so a press that never moves leaves
     * sizing untouched.
     */
    const startDrag = ({
        startX,
        onEnd,
    }: {
        startX: number;
        onEnd?: (end: { didMove: boolean }) => void;
    }) => {
        const startWidth = getRenderedWidth();
        const maxWidth = getMaxWidth(startWidth);
        let release: (() => void) | undefined;
        table.setColumnResizing((old) => ({
            ...old,
            isResizingColumn: column.id,
        }));
        const handleMove = (move: PointerEvent) => {
            release ??= commitWidths();
            setWidth(
                Math.min(
                    startWidth + (move.clientX - startX) * direction,
                    maxWidth,
                ),
            );
        };
        const handleEnd = () => {
            document.removeEventListener("pointermove", handleMove);
            document.removeEventListener("pointerup", handleEnd);
            document.removeEventListener("pointercancel", handleEnd);
            release?.();
            table.setColumnResizing((old) => ({
                ...old,
                isResizingColumn: false,
            }));
            onEnd?.({ didMove: release !== undefined });
        };
        document.addEventListener("pointermove", handleMove);
        document.addEventListener("pointerup", handleEnd);
        document.addEventListener("pointercancel", handleEnd);
    };

    return { fitToContent, resizeBy, startDrag };
};
